import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Eye, Filter, MoreVertical, DollarSign, Crown } from 'lucide-react';
import { useApiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const accent = '#6366f1';
const accentLight = '#f1f5ff';

const Users = () => {
  const { createUser, getAllUsers, updateUser, deleteUser, createCredit, getUserBalance, promoteToAdmin, getAllShowrooms } = useApiService();
  const { user: currentUser } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [itemsPerPage] = useState(10);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userBalances, setUserBalances] = useState({});
  const [roleFilter, setRoleFilter] = useState('all');
  const [showroomFilter, setShowroomFilter] = useState('all');
  const [showrooms, setShowrooms] = useState([]);
  const [formError, setFormError] = useState(null);
  const [creditForm, setCreditForm] = useState({
    credits: '',
    agentId: ''
  });

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'user',
    showroomId: ''
  });

  // Load users and showrooms on component mount
  useEffect(() => {
    loadUsers();
    loadShowrooms();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await getAllUsers();
      setUsers(response);
      setError(null);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadShowrooms = async () => {
    try {
      const response = await getAllShowrooms();
      setShowrooms(response);
    } catch (err) {
      console.error('Error loading showrooms:', err);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesShowroom = showroomFilter === 'all' || user.showroomId === showroomFilter;
    
    return matchesSearch && matchesRole && matchesShowroom;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getPasswordValidationError = (password) => {
    if (!password) return null; // Password is optional when creating/editing

    const unmetRules = [];
    if (password.length < 8) unmetRules.push('at least 8 characters');
    if (!/[A-Z]/.test(password)) unmetRules.push('one uppercase letter');
    if (!/[a-z]/.test(password)) unmetRules.push('one lowercase letter');
    if (!/[0-9]/.test(password)) unmetRules.push('one number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) unmetRules.push('one special character');

    if (unmetRules.length) {
      return `Password must include ${unmetRules.join(', ')}.`;
    }

    return null;
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match!');
      return;
    }

    const passwordError = getPasswordValidationError(formData.password);
    if (passwordError) {
      alert(passwordError);
      return;
    }
    
    if (!formData.showroomId) {
      alert('Please select a showroom. Showroom assignment is required.');
      return;
    }

    try {
      setSubmitting(true);
      const userData = {
        name: formData.fullName,
        email: formData.email,
        role: formData.role,
        showroomId: formData.showroomId || null,
      };

      // Only include password if provided
      if (formData.password) {
        userData.password = formData.password;
      }

      const newUser = await createUser(userData);
      
      // If user was created successfully, reload the users list
      if (newUser) {
        await loadUsers();
        setFormData({
          fullName: '',
          email: '',
          password: '',
          confirmPassword: '',
          role: 'user',
          showroomId: ''
        });
        setFormError(null);
        setShowAddModal(false);
      }
    } catch (err) {
      console.error('Error creating user:', err);
      // Extract the error message from the API response
      const errorMessage = err.message || err.details?.message || 'Failed to create user. Please try again.';
      setFormError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match!');
      return;
    }

    const passwordError = getPasswordValidationError(formData.password);
    if (passwordError) {
      alert(passwordError);
      return;
    }
    
    if (!formData.showroomId) {
      alert('Please select a showroom. Showroom assignment is required.');
      return;
    }

    // Check if trying to demote the last admin user
    if (editingUser.role === 'admin' && formData.role === 'user') {
      const adminUsers = users.filter(u => u.role === 'admin');
      if (adminUsers.length === 1) {
        alert('Cannot demote the last admin user. At least one admin user must remain in the system.');
        return;
      }
    }

    // Check if editingUser has a valid ID
    if (!editingUser || (!editingUser._id && !editingUser.id)) {
      alert('Invalid user data. Please try again.');
      return;
    }

    try {
      setSubmitting(true);
      const userData = {
        name: formData.fullName,
        email: formData.email,
        role: formData.role,
        showroomId: formData.showroomId || null,
      };

      // Only include password if provided
      if (formData.password) {
        userData.password = formData.password;
      }

      // Use either _id or id, whichever is available
      const userId = editingUser._id || editingUser.id;
      console.log('Updating user with ID:', userId, 'Data:', userData);
      
      await updateUser(userId, userData);
      await loadUsers();
      
      setFormData({
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'user',
        showroomId: ''
      });
      setFormError(null);
      setShowEditModal(false);
      setEditingUser(null);
    } catch (err) {
      console.error('Error updating user:', err);
      // Extract the error message from the API response
      const errorMessage = err.message || err.details?.message || 'Failed to update user. Please try again.';
      setFormError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    const user = users.find(u => (u._id || u.id) === userId);
    if (!user) return;

    // Check if trying to delete the last admin user
    if (user.role === 'admin') {
      const adminUsers = users.filter(u => u.role === 'admin');
      if (adminUsers.length === 1) {
        alert('Cannot delete the last admin user. At least one admin user must remain in the system.');
        return;
      }
    }

    if (window.confirm(`Are you sure you want to delete user "${user.name}"?\n\nThis action cannot be undone.`)) {
      try {
        await deleteUser(userId);
        await loadUsers();
      } catch (err) {
        console.error('Error deleting user:', err);
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  const handlePromoteToAdmin = async (userId) => {
    const user = users.find(u => (u._id || u.id) === userId);
    if (!user) return;
    
    // Check if there are already too many admin users (optional limit)
    const adminUsers = users.filter(u => u.role === 'admin');
    if (adminUsers.length >= 5) { // Limit to 5 admin users
      alert('Maximum number of admin users reached (5). Please contact system administrator if you need more admin users.');
      return;
    }
    
    if (window.confirm(`Are you sure you want to promote "${user.name}" to admin role?\n\nThis will give them full access to all system features including user management.`)) {
      try {
        await promoteToAdmin(userId);
        await loadUsers();
        alert(`User "${user.name}" has been promoted to admin successfully!`);
      } catch (err) {
        console.error('Error promoting user:', err);
        alert('Failed to promote user. Please try again.');
      }
    }
  };

  const openEditModal = (user) => {
    console.log('Opening edit modal for user:', user);
    setEditingUser(user);
    setFormData({
      fullName: user.name || '',
      email: user.email || '',
      password: '',
      confirmPassword: '',
      role: user.role || 'user',
      showroomId: user.showroomId || ''
    });
    setShowEditModal(true);
  };

  const openViewModal = (user) => {
    setViewingUser(user);
    setShowViewModal(true);
  };

  const handleCloseModal = () => {
    setFormData({
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'user',
      showroomId: ''
    });
    setFormError(null);
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingUser(null);
    setViewingUser(null);
    setShowroomFilter('all');
  };

  const openCreditModal = async (user) => {
    setSelectedUser(user);
    setCreditForm({
      credits: '',
      agentId: currentUser?.id || ''
    });
    
    // Get user's current balance
    try {
      const balance = await getUserBalance(user._id);
      setUserBalances(prev => ({
        ...prev,
        [user._id]: balance.currentBalance
      }));
    } catch (err) {
      console.error('Error getting user balance:', err);
    }
    
    setShowCreditModal(true);
  };

  const handleCreditInputChange = (e) => {
    const { name, value } = e.target;
    setCreditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddCredit = async (e) => {
    e.preventDefault();
    
    if (!creditForm.credits || creditForm.credits === '0') {
      alert('Please enter a valid credit amount');
      return;
    }

    // AgentId is optional for now

    try {
      setSubmitting(true);
      const creditData = {
        userId: selectedUser._id,
        credits: parseFloat(creditForm.credits),
        agentId: creditForm.agentId || selectedUser._id // Use provided agentId or user's own ID as fallback
      };

      await createCredit(creditData);
      
      // Refresh user balances
      const balance = await getUserBalance(selectedUser._id);
      setUserBalances(prev => ({
        ...prev,
        [selectedUser._id]: balance.currentBalance
      }));
      
      setCreditForm({
        credits: '',
        agentId: currentUser?.id || ''
      });
      setShowCreditModal(false);
      setSelectedUser(null);
      
      alert('Credit transaction completed successfully!');
    } catch (err) {
      console.error('Error adding credit:', err);
      alert('Failed to add credit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: 40, 
            height: 40, 
            border: '4px solid #e5e7eb', 
            borderTop: '4px solid #6366f1', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p style={{ color: '#6b7280', margin: 0 }}>Loading users...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#dc2626', margin: '0 0 1rem' }}>{error}</p>
          <button 
            onClick={loadUsers}
            style={{
              background: '#6366f1',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '0.5rem 1rem',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', 
      background: '#fafbfc', 
      minHeight: '100vh',
      padding: '2rem 1.5rem',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Header Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '.5rem' }}>
        <div>
          <h1 className="fw-bold mb-2" style={{ color: '#1f2937', fontSize: '1.75rem', letterSpacing: '-0.025em' }}>Users Management</h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Manage and monitor your application users</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button 
            className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold"
            style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem' }}
          >
            <Filter size={16} />
            <span className="d-none d-sm-inline">Filter</span>
          </button>
          <button 
            className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold"
            style={{ background: accent, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.875rem', boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)' }}
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={16} />
            <span className="d-none d-sm-inline">Add User</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '.5rem' }}>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: accentLight, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: accent, fontSize: '1rem', fontWeight: '600' }}>{users.length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Users</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>All registered</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0fdf4', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#16a34a', fontSize: '1rem', fontWeight: '600' }}>{users.filter(u => u.status === 'active').length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Active Users</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Currently active</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#fef2f2', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#dc2626', fontSize: '1rem', fontWeight: '600' }}>{users.filter(u => u.status === 'inactive').length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Inactive Users</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Suspended</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0f9ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#0284c7', fontSize: '1rem', fontWeight: '600' }}>{filteredUsers.length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Filtered Results</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Search results</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#fef3c7', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#d97706', fontSize: '1rem', fontWeight: '600' }}>{users.filter(u => u.role === 'admin').length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Admin Users</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Administrators</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card border-0 shadow-sm mb-4" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
        <div className="card-body p-3 p-md-4">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="position-relative">
                <Search 
                  size={18} 
                  style={{ color: '#9ca3af', position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', zIndex: 1 }} 
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search users by name, company, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', paddingLeft: '48px', fontSize: '0.875rem' }}
                />
              </div>
            </div>
            <div style={{ minWidth: 150 }}>
              <select
                className="form-control"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                style={{ 
                  background: '#f9fafb', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px', 
                  padding: '0.75rem', 
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </select>
            </div>
            <div style={{ minWidth: 150 }}>
              <select
                className="form-control"
                value={showroomFilter}
                onChange={(e) => setShowroomFilter(e.target.value)}
                style={{ 
                  background: '#f9fafb', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px', 
                  padding: '0.75rem', 
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Showrooms</option>
                {showrooms.map(showroom => (
                  <option key={showroom._id || showroom.id} value={showroom._id || showroom.id}>
                    {showroom.showroomName}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: 180, textAlign: 'right', flex: 'none' }}>
              <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                Showing {filteredUsers.length} of {users.length} users
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card border-0 shadow-sm" style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', width: '100%' }}>
        <div className="table-responsive">
          <table className="table mb-0" style={{ width: '100%' }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th className="border-0 fw-semibold text-muted py-2 px-2" style={{ fontSize: '0.875rem' }}>
                  <span className="d-none d-md-inline">User</span>
                  <span className="d-md-none">Name</span>
                </th>

                <th className="border-0 fw-semibold text-muted py-2 d-none d-lg-table-cell" style={{ fontSize: '0.875rem' }}>
                  Email
                </th>
                <th className="border-0 fw-semibold text-muted py-2" style={{ fontSize: '0.875rem' }}>
                  Status
                </th>
                <th className="border-0 fw-semibold text-muted py-2" style={{ fontSize: '0.875rem' }}>
                  Role
                </th>
                <th className="border-0 fw-semibold text-muted py-2 d-none d-md-table-cell" style={{ fontSize: '0.875rem' }}>
                  Showroom
                </th>
                <th className="border-0 fw-semibold text-muted py-2 d-none d-md-table-cell" style={{ fontSize: '0.875rem' }}>
                  Created
                </th>
                <th className="border-0 fw-semibold text-muted py-2 text-end" style={{ fontSize: '0.875rem' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map((user, index) => (
                <tr key={user._id || user.id} style={{ borderBottom: index === currentUsers.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                  <td className="py-2">
                    <div className="d-flex align-items-center gap-2 gap-md-3">
                      <div style={{ width: 32, height: 32, background: `linear-gradient(135deg, ${accent} 0%, #8b5cf6 100%)`, color: '#fff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', fontSize: '0.875rem', flexShrink: 0 }}>
                        {(user.name || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-grow-1 min-w-0">
                        <div className="fw-semibold text-truncate" style={{ color: '#1f2937', fontSize: '0.875rem' }}>
                          {user.name || 'Unknown User'}
                          {user.id === currentUser?.id && (
                            <span style={{ marginLeft: '0.5rem', color: '#6366f1', fontSize: '0.75rem', fontWeight: '500' }}>
                              (You)
                            </span>
                          )}
                        </div>
                        <div className="text-muted d-none d-md-block" style={{ fontSize: '0.75rem' }}>
                          ID: {user._id || user.id}
                        </div>

                      </div>
                    </div>
                  </td>

                  <td className="py-2 d-none d-lg-table-cell">
                    <span className="text-truncate d-block" style={{ color: '#374151', fontSize: '0.875rem' }}>
                      {user.email || 'No Email'}
                    </span>
                  </td>
                  <td className="py-2">
                    <span className={`badge px-2 py-1 px-md-3 py-md-2 rounded-pill fw-semibold`} style={{ background: user.status === 'active' ? '#f0fdf4' : '#fef2f2', color: user.status === 'active' ? '#16a34a' : '#dc2626', fontSize: '0.75rem' }}>
                      {user.status || 'Unknown'}
                    </span>
                  </td>
                  <td className="py-2">
                    <span className={`badge px-2 py-1 px-md-3 py-md-2 rounded-pill fw-semibold`} style={{ background: user.role === 'admin' ? '#f0fdf4' : '#fef2f2', color: user.role === 'admin' ? '#16a34a' : '#dc2626', fontSize: '0.75rem' }}>
                      {user.role || 'User'}
                    </span>
                  </td>
                  <td className="py-2 d-none d-md-table-cell">
                    <span className="text-truncate d-block" style={{ color: '#374151', fontSize: '0.875rem' }}>
                      {user.showroomId ? showrooms.find(s => (s._id || s.id) === user.showroomId)?.showroomName || 'Unknown Showroom' : 'No Showroom'}
                    </span>
                  </td>
                  <td className="py-2 d-none d-md-table-cell">
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                    </span>
                  </td>
                  <td className="py-2 text-end">
                    <div className="btn-group gap-1" role="group">
                      <button 
                        className="btn btn-sm rounded-pill border-0"
                        style={{ 
                          color: '#16a34a', 
                          background: '#f0fdf4',
                          padding: '4px 6px',
                          fontSize: '0.75rem'
                        }}
                        title="Add/Deduct Credits"
                        onClick={() => openCreditModal(user)}
                      >
                        <DollarSign size={12} />
                      </button>
                      <button 
                        className="btn btn-sm rounded-pill border-0"
                        style={{ 
                          color: '#6b7280', 
                          background: '#f9fafb',
                          padding: '4px 6px',
                          fontSize: '0.75rem'
                        }}
                        title="View"
                        onClick={() => openViewModal(user)}
                      >
                        <Eye size={12} />
                      </button>
                      <button 
                        className="btn btn-sm rounded-pill border-0"
                        style={{ 
                          color: '#f59e0b', 
                          background: '#fffbeb',
                          padding: '4px 6px',
                          fontSize: '0.75rem'
                        }}
                        title="Edit"
                        onClick={() => openEditModal(user)}
                      >
                        <Edit size={12} />
                      </button>
                      <button 
                        className="btn btn-sm rounded-pill border-0"
                        style={{ 
                          color: '#ef4444', 
                          background: '#fef2f2',
                          padding: '4px 6px',
                          fontSize: '0.75rem'
                        }}
                        title="Delete"
                        onClick={() => handleDeleteUser(user._id || user.id)}
                      >
                        <Trash2 size={12} />
                      </button>
                      <button 
                        className="btn btn-sm rounded-pill border-0"
                        style={{ 
                          color: '#6b7280', 
                          background: '#f9fafb',
                          padding: '4px 6px',
                          fontSize: '0.75rem'
                        }}
                        title="More"
                      >
                        <MoreVertical size={12} />
                      </button>
                      {user.role !== 'admin' && (
                        <button 
                          className="btn btn-sm rounded-pill border-0"
                          style={{ 
                            color: '#f59e0b', 
                            background: '#fffbeb',
                            padding: '4px 6px',
                            fontSize: '0.75rem'
                          }}
                          title="Promote to Admin"
                          onClick={() => handlePromoteToAdmin(user._id || user.id)}
                        >
                          <Crown size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="card-footer bg-white border-0 py-3 py-md-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <div className="text-muted text-center text-md-start" style={{ fontSize: '0.875rem' }}>
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredUsers.length)} of {filteredUsers.length} results
            </div>
            <nav aria-label="Users pagination">
              <ul className="pagination mb-0 gap-1">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link border-0 px-2 px-md-3 py-2 rounded"
                    style={{ 
                      color: currentPage === 1 ? '#9ca3af' : accent,
                      background: 'transparent',
                      fontSize: '0.875rem'
                    }}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <span className="d-none d-sm-inline">Previous</span>
                    <span className="d-sm-none">‹</span>
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                    <button 
                      className={`page-link border-0 px-2 px-md-3 py-2 rounded fw-semibold${currentPage === page ? '' : ''}`}
                      style={{ 
                        color: currentPage === page ? '#fff' : accent, 
                        background: currentPage === page ? accent : 'transparent',
                        fontSize: '0.875rem'
                      }}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link border-0 px-2 px-md-3 py-2 rounded"
                    style={{ 
                      color: currentPage === totalPages ? '#9ca3af' : accent,
                      background: 'transparent',
                      fontSize: '0.875rem'
                    }}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <span className="d-none d-sm-inline">Next</span>
                    <span className="d-sm-none">›</span>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '12px', padding: '2rem', minWidth: 320, maxWidth: 480, width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', border: '1px solid #e5e7eb' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ color: '#1f2937', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Add New User</h4>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>Create a new user account with the required information</p>
            </div>
            <form onSubmit={handleAddUser}>
              {formError && (
                <div style={{ 
                  marginBottom: '1rem', 
                  padding: '0.75rem', 
                  background: '#fef2f2', 
                  border: '1px solid #fecaca', 
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.25rem' }}>⚠️</span>
                  <span style={{ color: '#991b1b', fontSize: '0.875rem', flex: 1 }}>{formError}</span>
                  <button
                    type="button"
                    onClick={() => setFormError(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#991b1b',
                      cursor: 'pointer',
                      fontSize: '1.25rem',
                      padding: 0,
                      lineHeight: 1
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Full Name</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="fullName" 
                    placeholder="Enter full name" 
                    value={formData.fullName} 
                    onChange={handleInputChange} 
                    required 
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Email</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    name="email" 
                    placeholder="Enter email address" 
                    value={formData.email} 
                    onChange={handleInputChange} 
                    required 
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Password</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    name="password" 
                    placeholder="Enter password (optional)" 
                    value={formData.password} 
                    onChange={handleInputChange} 
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  />
                  <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                    Leave empty to generate a default password. Password must be at least 8 characters with uppercase, lowercase, number, and special character.
                  </small>
                </div>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Confirm Password</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    name="confirmPassword" 
                    placeholder="Confirm password" 
                    value={formData.confirmPassword} 
                    onChange={handleInputChange} 
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Role</label>
                  <select 
                    className="form-control" 
                    name="role" 
                    value={formData.role} 
                    onChange={handleInputChange} 
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                  <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                    Admin users have full access to all system features
                  </small>
                </div>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Showroom *</label>
                  <select 
                    className="form-control" 
                    name="showroomId" 
                    value={formData.showroomId} 
                    onChange={handleInputChange} 
                    required
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  >
                    <option value="">Select a showroom (required)</option>
                    {showrooms.map(showroom => (
                      <option key={showroom._id || showroom.id} value={showroom._id || showroom.id}>
                        {showroom.showroomName}
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                    Assign user to a specific showroom (required)
                  </small>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  onClick={handleCloseModal}
                  style={{ 
                    background: '#f9fafb', 
                    color: '#374151', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '0.75rem 1.5rem', 
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submitting}
                  style={{ 
                    background: accent, 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: '8px', 
                    padding: '0.75rem 1.5rem', 
                    fontSize: '0.875rem',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.6 : 1
                  }}
                >
                  {submitting ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '12px', padding: '2rem', minWidth: 320, maxWidth: 480, width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', border: '1px solid #e5e7eb' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ color: '#1f2937', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Edit User</h4>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>Update user information</p>
            </div>
            <form onSubmit={handleEditUser}>
              {formError && (
                <div style={{ 
                  marginBottom: '1rem', 
                  padding: '0.75rem', 
                  background: '#fef2f2', 
                  border: '1px solid #fecaca', 
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.25rem' }}>⚠️</span>
                  <span style={{ color: '#991b1b', fontSize: '0.875rem', flex: 1 }}>{formError}</span>
                  <button
                    type="button"
                    onClick={() => setFormError(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#991b1b',
                      cursor: 'pointer',
                      fontSize: '1.25rem',
                      padding: 0,
                      lineHeight: 1
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Full Name</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="fullName" 
                    placeholder="Enter full name" 
                    value={formData.fullName} 
                    onChange={handleInputChange} 
                    required 
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Email</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    name="email" 
                    disabled={true}
                    placeholder="Enter email address" 
                    value={formData.email} 
                    onChange={handleInputChange} 
                    required 
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Password</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    name="password" 
                    placeholder="Enter new password (optional)" 
                    value={formData.password} 
                    onChange={handleInputChange} 
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  />
                  <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                    Leave empty to keep current password. Password must be at least 8 characters with uppercase, lowercase, number, and special character.
                  </small>
                </div>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Confirm Password</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    name="confirmPassword" 
                    placeholder="Confirm password" 
                    value={formData.confirmPassword} 
                    onChange={handleInputChange} 
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Role</label>
                  <select 
                    className="form-control" 
                    name="role" 
                    value={formData.role} 
                    onChange={handleInputChange} 
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                  <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                    Admin users have full access to all system features
                  </small>
                </div>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Showroom *</label>
                  <select 
                    className="form-control" 
                    name="showroomId" 
                    value={formData.showroomId} 
                    onChange={handleInputChange} 
                    required
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  >
                    <option value="">Select a showroom (required)</option>
                    {showrooms.map(showroom => (
                      <option key={showroom._id || showroom.id} value={showroom._id || showroom.id}>
                        {showroom.showroomName}
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                    Assign user to a specific showroom (required)
                  </small>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  onClick={handleCloseModal}
                  style={{ 
                    background: '#f9fafb', 
                    color: '#374151', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '0.75rem 1.5rem', 
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submitting}
                  style={{ 
                    background: accent, 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: '8px', 
                    padding: '0.75rem 1.5rem', 
                    fontSize: '0.875rem',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.6 : 1
                  }}
                >
                  {submitting ? 'Updating...' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View User Modal */}
      {showViewModal && viewingUser && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '12px', padding: '2rem', minWidth: 320, maxWidth: 480, width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', border: '1px solid #e5e7eb' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ color: '#1f2937', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>User Details</h4>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>View user information</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Full Name</label>
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}>
                  {viewingUser.name || 'Not provided'}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Email</label>
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}>
                  {viewingUser.email || 'Not provided'}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Status</label>
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}>
                  <span className={`badge px-2 py-1 rounded-pill fw-semibold`} style={{ background: viewingUser.status === 'active' ? '#f0fdf4' : '#fef2f2', color: viewingUser.status === 'active' ? '#16a34a' : '#dc2626', fontSize: '0.75rem' }}>
                    {viewingUser.status || 'Unknown'}
                  </span>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Role</label>
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}>
                  <span className={`badge px-2 py-1 rounded-pill fw-semibold`} style={{ background: viewingUser.role === 'admin' ? '#f0fdf4' : '#fef2f2', color: viewingUser.role === 'admin' ? '#16a34a' : '#dc2626', fontSize: '0.75rem' }}>
                    {viewingUser.role || 'User'}
                  </span>
                  <span style={{ marginLeft: '0.5rem', color: '#6b7280', fontSize: '0.75rem' }}>
                    {viewingUser.role === 'admin' ? 'Full system access' : 'Limited access'}
                  </span>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Created At</label>
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}>
                  {viewingUser.createdAt ? new Date(viewingUser.createdAt).toLocaleString() : 'Unknown'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button 
                onClick={handleCloseModal}
                style={{ 
                  background: accent, 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '8px', 
                  padding: '0.75rem 1.5rem', 
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Deduct Credit Modal */}
      {showCreditModal && selectedUser && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '12px', padding: '2rem', minWidth: 320, maxWidth: 480, width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', border: '1px solid #e5e7eb' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ color: '#1f2937', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Manage Credits</h4>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                Add or deduct credits for {selectedUser.name}
              </p>
              {userBalances[selectedUser._id] !== undefined && (
                <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                  <span style={{ color: '#0284c7', fontSize: '0.875rem', fontWeight: '500' }}>
                    Current Balance: ${userBalances[selectedUser._id]}
                  </span>
                </div>
              )}
            </div>
            <form onSubmit={handleAddCredit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                    Credit Amount
                  </label>
                  <div style={{ marginBottom: '0.25rem' }}>
                    <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                      Enter positive number to add credits, negative number to deduct credits
                    </small>
                  </div>
                  <input 
                    type="number" 
                    className="form-control" 
                    name="credits" 
                    placeholder="e.g., 100 or -50" 
                    value={creditForm.credits} 
                    onChange={handleCreditInputChange} 
                    required 
                    step="0.01"
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  />
                </div>
                {parseFloat(creditForm.credits) < 0 && (
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                      Agent ID
                    </label>
                    <div style={{ marginBottom: '0.25rem' }}>
                      <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                        Optional - for tracking who processed the deduction
                      </small>
                    </div>
                    <input 
                      type="text" 
                      className="form-control" 
                      name="agentId" 
                      placeholder="Enter agent ID" 
                      value={creditForm.agentId} 
                      onChange={handleCreditInputChange} 
                      required={false}
                      style={{ 
                        background: '#f9fafb', 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '8px', 
                        padding: '0.75rem', 
                        fontSize: '0.875rem',
                        transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                      }}
                    />
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowCreditModal(false);
                    setSelectedUser(null);
                    setCreditForm({ credits: '', agentId: '' });
                  }}
                  style={{ 
                    background: '#f9fafb', 
                    color: '#374151', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '0.75rem 1.5rem', 
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submitting}
                  style={{ 
                    background: accent, 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: '8px', 
                    padding: '0.75rem 1.5rem', 
                    fontSize: '0.875rem',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.6 : 1
                  }}
                >
                  {submitting ? 'Processing...' : 'Process Credit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
